import { useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { Card, Col, Row, Tooltip } from 'antd';
import { useTranslate, TFnType } from '@tolgee/react';
import { initEcharts, EChartsOption, applyChartTheme } from 'lib/echarts';
import dayjs from 'lib/dayjs';
import { QuestionCircleOutlined } from '@ant-design/icons';
import {
  numDisplay,
  RMBSymbol,
  currencyDisplay,
  getAmountByCurrency,
  currencyDisplayWithUnit,
  tooltipRender,
} from 'views/Dashboard/ImplementData/utils';
import { useDashboardCurrency } from 'views/Dashboard/ImplementData/hooks/useCurrency';
import Empty from 'components/Empty';
import { RegionFixedExchangeRateValue } from 'services/config/region';
import { useSize } from 'ahooks';
import { useDashboardPlatformTips } from '../Filter/useFilter';

const Container = styled(Card)`
  margin-top: 16px;
  border-radius: 4px;
  height: 420px;
  background: var(--fett-bg-panel);
  border-color: var(--fett-border-default);
  .ant-card-head {
    border-bottom-color: var(--fett-border-default);
  }
  .ant-card-head-title {
    color: var(--fett-text-primary);
  }
  width: 100%;
  .ant-card-head {
    .ant-card-head-title {
      .before {
        height: 14px;
        border-left: 4px solid #3370ff;
        display: inline-block;
        margin-right: 4px;
      }
      .anticon {
        margin-left: 4px;
      }
    }
  }
  .ant-card-body {
    display: flex;
    padding: 12px 24px;
    .ant-row {
      height: 50px;
      margin-bottom: 12px;
      background-color: var(--fett-bg-subtle);
      border-radius: 4px;
      align-items: center;
      padding: 0 16px;
      .title {
        color: var(--fett-text-tertiary);
      }
      .num {
        color: var(--fett-text-secondary);
        font-size: 16px;
      }
    }
  }
`;
const Relative = styled.div`
  position: relative;
  width: 100%;
  .ant-empty {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
  }
  .el {
    height: 320px;
    width: 100%;
  }
`;
const Left = styled.div`
  width: 380px;
  .pie {
    width: 380px;
    height: 210px;
  }
`;
interface Props {
  value?: Dashboard.OrderOverviewInner;
}
const barOption = (
  value: Dashboard.OrderOverviewInner,
  t: TFnType,
  tips: ReturnType<typeof useDashboardPlatformTips>,
  currency?: RegionFixedExchangeRateValue,
): EChartsOption => {
  const data = value.静默非静默单每日 || [];
  const dateRange = data.map((x) => x.date).map((x) => dayjs(x).format('MM-DD'));
  const dataZoom = [
    {
      show: true,
      realtime: true,
      start: 0,
      end: 100,
    },
    {
      type: 'inside',
      realtime: true,
      start: 0,
      end: 100,
    },
  ];
  return {
    dataZoom: dateRange.length > 10 ? dataZoom : undefined,
    legend: {
      data: [
        {
          name: t('ViewDashboard.静默单下单量'),
          tooltip: {
            show: Boolean(tips.静默下单量tips),
            formatter: tooltipRender(tips.静默下单量tips),
          },
        },
        t('ViewDashboard.非静默单下单量'),
        {
          name: t('ViewDashboard.静默单下单总金额'),
          tooltip: {
            show: Boolean(tips.静默下单总金额tips),
            formatter: tooltipRender(tips.静默下单总金额tips),
          },
        },
        t('ViewDashboard.非静默单下单总金额'),
      ],
    },
    xAxis: {
      type: 'category',
      boundaryGap: true,
      data: dateRange,
    },
    yAxis: [
      {
        type: 'value',
        name: '',
      },
      {
        type: 'value',
        name: `(${currency?.symbol ?? RMBSymbol})`,
        nameTextStyle: {
          align: 'left',
        },
      },
    ],
    tooltip: {
      trigger: 'axis',
    },
    series: [
      {
        data: data.map((x) => x.静默单下单量),
        type: 'bar',
        name: t('ViewDashboard.静默单下单量'),
        itemStyle: {
          opacity: 0.85,
          color: '#5AD8A6',
        },
      },
      {
        data: data.map((x) => x.非静默单下单量),
        type: 'bar',
        name: t('ViewDashboard.非静默单下单量'),
        itemStyle: {
          opacity: 0.85,
          color: '#5B8FF9',
        },
      },
      {
        data: data.map((x) => getAmountByCurrency(currency?.shorthand, x.静默单下单总金额)),
        type: 'line',
        yAxisIndex: 1,
        smooth: true,
        tooltip: {
          valueFormatter: (value) =>
            `${currency?.symbol ?? RMBSymbol}${currencyDisplay(Number(value))}`,
        },
        name: t('ViewDashboard.静默单下单总金额'),
        itemStyle: {
          color: '#f6903d',
        },
        lineStyle: {
          color: '#f6903d',
        },
      },
      {
        data: data.map((x) => getAmountByCurrency(currency?.shorthand, x.非静默单下单总金额)),
        type: 'line',
        yAxisIndex: 1,
        smooth: true,
        tooltip: {
          valueFormatter: (value) =>
            `${currency?.symbol ?? RMBSymbol}${currencyDisplay(Number(value))}`,
        },
        itemStyle: {
          color: '#7262fd',
        },
        lineStyle: {
          color: '#7262fd',
        },
        name: t('ViewDashboard.非静默单下单总金额'),
      },
    ],
  };
};
const pieOption = (v: Dashboard.OrderOverviewInner, t: TFnType): EChartsOption => {
  return {
    backgroundColor: '#f5f9fe',

    series: [
      {
        type: 'pie',
        radius: '50%',
        label: {
          formatter: '{b}\n{c} ({d}%)',
        },
        data: [
          {
            value: v.静默单下单量,
            name: t('ViewDashboard.静默单下单量'),
            itemStyle: {
              opacity: 0.85,
              color: '#5AD8A6',
            },
            label: {
              overflow: 'break',
            },
          },
          {
            value: v.非静默单下单量,
            name: t('ViewDashboard.非静默单下单量'),
            itemStyle: {
              color: '#5B8FF9',
              opacity: 0.85,
            },
            label: {
              overflow: 'break',
            },
          },
        ],
      },
    ],
  };
};

interface Props {
  value?: Dashboard.OrderOverviewInner;
}
const Quiesce = (props: Props) => {
  const { value } = props;
  const { t } = useTranslate();
  const { currency } = useDashboardCurrency();
  const chart1DomRef = useRef<HTMLDivElement>(null);
  const chart2DomRef = useRef<HTMLDivElement>(null);
  const chart1Ref = useRef<echarts.ECharts>();
  const chart2Ref = useRef<echarts.ECharts>();

  const { width: width1 } = useSize(chart1DomRef) || {};
  const { width: width2 } = useSize(chart2DomRef) || {};

  useEffect(() => {
    chart1Ref.current?.resize();
  }, [width1]);
  useEffect(() => {
    chart2Ref.current?.resize();
  }, [width2]);
  useEffect(() => {
    if (!value || !chart1DomRef.current) {
      return;
    }
    if (!chart1Ref.current) {
      chart1Ref.current = initEcharts(chart1DomRef.current);
    }
    const options = pieOption(value, t);
    chart1Ref.current.setOption(applyChartTheme(options));
  }, [value, t, currency]);

  const tips = useDashboardPlatformTips();

  useEffect(() => {
    if (!value || !chart2DomRef.current) {
      return;
    }
    if (!chart2Ref.current) {
      chart2Ref.current = initEcharts(chart2DomRef.current);
    }
    const options = barOption(value, t, tips, currency);
    chart2Ref.current.setOption(applyChartTheme(options));
  }, [value, t, tips, currency]);

  return (
    <Container
      title={
        <div>
          <span className="before" />

          {t('ViewDashboard.静默非静默单情况')}
          <Tooltip title={t('ViewDashboard.静默非静默单tooltip')}>
            <QuestionCircleOutlined />
          </Tooltip>
        </div>
      }
    >
      <Left>
        <Row>
          <Col span={10} className="title">
            {t('ViewDashboard.下单总量')}
            {tips.下单总量tips && (
              <Tooltip title={tips.下单总量tips}>
                <QuestionCircleOutlined className="ml-1" />
              </Tooltip>
            )}
          </Col>
          <Col span={14} className="num">
            {numDisplay(value?.静默非静默下单总量)}
          </Col>
        </Row>
        <Row>
          <Col span={10} className="title">
            {t('ViewDashboard.下单总金额')}
            {tips.下单总金额tips && (
              <Tooltip title={tips.下单总金额tips}>
                <QuestionCircleOutlined className="ml-1" />
              </Tooltip>
            )}
          </Col>
          <Col span={14} className="num">
            {currencyDisplayWithUnit(currency?.shorthand, value?.静默非静默单下单总金额)}
          </Col>
        </Row>
        <Relative className="pie">
          {!value?.静默单下单量 && !value?.非静默单下单量 ? <Empty /> : null}
          <div
            className="pie"
            ref={chart1DomRef}
            style={{ display: !value?.静默单下单量 && !value?.非静默单下单量 ? 'none' : 'block' }}
          />
        </Relative>
      </Left>
      <Relative>
        <div className="el" ref={chart2DomRef} />
        {!value?.静默非静默单每日?.length ? <Empty /> : null}
      </Relative>
    </Container>
  );
};

export default Quiesce;
